import { dateFormatPreference, formatDate, type DateFormatPreference } from "../../ui/date.ts";
import { normalizeMobileActions } from "../navigation/qol-preferences.ts";
import { appearanceDialogView } from "./view.ts";

export interface AppearanceCapabilities {
  mobileActions(): string[];
  setMobileActions(actions: string[]): void;
  renderShell(): Promise<void>;
  toast(message: string, error?: boolean): void;
}

export function restoreAppearancePreferences(document: Document, storage: Pick<Storage, "getItem">): void {
  const defaults = {
    density: "comfortable", contrast: "standard", oled: "off", noteTint: "subtle", noteTitles: "off",
    sidebarWidth: "standard", accent: "violet", taskSwipe: "off",
  } as const;
  for (const [key, fallback] of Object.entries(defaults)) document.documentElement.dataset[key] = storage.getItem(key) || fallback;
}

export function openAppearanceDialog(capabilities: AppearanceCapabilities): void {
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog appearance-dialog";
  dialog.setAttribute("aria-label", "Appearance");
  dialog.innerHTML = appearanceDialogView({ values: {
    density: document.documentElement.dataset.density || "comfortable",
    contrast: document.documentElement.dataset.contrast || "standard",
    oled: document.documentElement.dataset.oled || "off",
    noteTint: document.documentElement.dataset.noteTint || "subtle",
    noteTitles: document.documentElement.dataset.noteTitles || "off",
    sidebarWidth: document.documentElement.dataset.sidebarWidth || "standard",
    accent: document.documentElement.dataset.accent || "violet",
    dateFormat: dateFormatPreference(),
    taskSwipe: document.documentElement.dataset.taskSwipe || "off",
  }, mobileActions: capabilities.mobileActions() });
  const form = dialog.querySelector<HTMLFormElement>("form")!;
  form.querySelector<HTMLSelectElement>("[name=dateFormat]")?.addEventListener("change", event => {
    form.querySelector<HTMLElement>("[data-date-format-preview]")!.textContent = `Example: ${formatDate("2026-10-24", (event.currentTarget as HTMLSelectElement).value as DateFormatPreference)}`;
  });
  form.querySelectorAll<HTMLInputElement>("[name=mobileActions]").forEach(input => input.addEventListener("change", () => {
    if (form.querySelectorAll("[name=mobileActions]:checked").length > 5) { input.checked = false; capabilities.toast("Choose up to five mobile actions", true); }
  }));
  form.addEventListener("submit", event => {
    event.preventDefault();
    const submitter = (event as SubmitEvent).submitter;
    if (submitter instanceof HTMLButtonElement && submitter.value !== "save") { dialog.close(); return; }
    const data = new FormData(form);
    for (const key of ["density", "contrast", "oled", "noteTint", "noteTitles", "sidebarWidth", "accent", "taskSwipe", "dateFormat"] as const) {
      const value = String(data.get(key)); localStorage.setItem(key, value); document.documentElement.dataset[key] = value;
    }
    const actions = normalizeMobileActions(data.getAll("mobileActions").map(String));
    capabilities.setMobileActions(actions); localStorage.setItem("mobileActions", actions.join(","));
    dialog.close(); void capabilities.renderShell().then(() => capabilities.toast("Appearance updated"));
  });
  document.body.append(dialog); dialog.addEventListener("close", () => dialog.remove()); dialog.showModal();
}
